/**
 * Discover Layer Filter Schema — field catalog, operators, and validation.
 *
 * This module defines the canonical rule schema used by the layer wizard
 * (Step 2) and later by backend persistence. All fields and operators are
 * allowlisted; arbitrary field injection is not possible.
 *
 * @see docs/IMPLEMENT_MARKER_LAYERS_DISCOVER.md §2.3, §2.6.2, §3.3
 */

// ---------------------------------------------------------------------------
// Value types and operator definitions
// ---------------------------------------------------------------------------

/** Supported value types for filterable fields. */
export const VALUE_TYPES = Object.freeze({
    STRING: 'STRING',
    ENUM: 'ENUM',
    NUMBER: 'NUMBER',
    DATE_TIME: 'DATE_TIME',
    BOOLEAN: 'BOOLEAN',
});

/**
 * Operators grouped by the value type they apply to.
 * Order within each array determines UI display order.
 */
export const OPERATORS_BY_TYPE = Object.freeze({
    [VALUE_TYPES.STRING]: ['EQUALS', 'NOT_EQUALS', 'CONTAINS', 'STARTS_WITH', 'IN'],
    [VALUE_TYPES.ENUM]: ['EQUALS', 'NOT_EQUALS', 'IN', 'NOT_IN'],
    [VALUE_TYPES.NUMBER]: ['EQUALS', 'NOT_EQUALS', 'GT', 'GTE', 'LT', 'LTE', 'BETWEEN'],
    [VALUE_TYPES.DATE_TIME]: ['BEFORE', 'AFTER', 'BETWEEN'],
    [VALUE_TYPES.BOOLEAN]: ['IS_TRUE', 'IS_FALSE'],
});

/** Human-readable i18n key suffix for each operator. */
export const OPERATOR_LABEL_KEYS = Object.freeze({
    EQUALS: 'discoverPage.layerWizard.filters.operators.equals',
    NOT_EQUALS: 'discoverPage.layerWizard.filters.operators.notEquals',
    CONTAINS: 'discoverPage.layerWizard.filters.operators.contains',
    STARTS_WITH: 'discoverPage.layerWizard.filters.operators.startsWith',
    IN: 'discoverPage.layerWizard.filters.operators.in',
    NOT_IN: 'discoverPage.layerWizard.filters.operators.notIn',
    GT: 'discoverPage.layerWizard.filters.operators.gt',
    GTE: 'discoverPage.layerWizard.filters.operators.gte',
    LT: 'discoverPage.layerWizard.filters.operators.lt',
    LTE: 'discoverPage.layerWizard.filters.operators.lte',
    BETWEEN: 'discoverPage.layerWizard.filters.operators.between',
    BEFORE: 'discoverPage.layerWizard.filters.operators.before',
    AFTER: 'discoverPage.layerWizard.filters.operators.after',
    IS_TRUE: 'discoverPage.layerWizard.filters.operators.isTrue',
    IS_FALSE: 'discoverPage.layerWizard.filters.operators.isFalse',
});

/** Operator fallback labels (used as defaultValue in t()). */
export const OPERATOR_LABEL_DEFAULTS = Object.freeze({
    EQUALS: 'equals',
    NOT_EQUALS: 'not equals',
    CONTAINS: 'contains',
    STARTS_WITH: 'starts with',
    IN: 'in list',
    NOT_IN: 'not in list',
    GT: 'greater than',
    GTE: 'greater or equal',
    LT: 'less than',
    LTE: 'less or equal',
    BETWEEN: 'between',
    BEFORE: 'before',
    AFTER: 'after',
    IS_TRUE: 'is true',
    IS_FALSE: 'is false',
});

// ---------------------------------------------------------------------------
// Field catalog per object type
// ---------------------------------------------------------------------------

/**
 * @typedef {Object} FieldDef
 * @property {string} id      - Stable field identifier (e.g. 'name', 'metadata.city')
 * @property {string} labelKey - i18n key
 * @property {string} labelDefault - Fallback label
 * @property {string} valueType - One of VALUE_TYPES
 * @property {string[]} [enumValues] - Allowed values when valueType is ENUM
 */

/** Fields common to ALL object types. */
const COMMON_FIELDS = [
    {
        id: 'name',
        labelKey: 'discoverPage.layerWizard.filters.fields.name',
        labelDefault: 'Name',
        valueType: VALUE_TYPES.STRING,
    },
    {
        id: 'privacy',
        labelKey: 'discoverPage.layerWizard.filters.fields.privacy',
        labelDefault: 'Visibility',
        valueType: VALUE_TYPES.ENUM,
        enumValues: ['PUBLIC', 'FRIENDS', 'ONLY_ME', 'PRIVATE'],
    },
    {
        id: 'metadata.city',
        labelKey: 'discoverPage.layerWizard.filters.fields.city',
        labelDefault: 'City',
        valueType: VALUE_TYPES.STRING,
    },
    {
        id: 'metadata.description',
        labelKey: 'discoverPage.layerWizard.filters.fields.description',
        labelDefault: 'Description',
        valueType: VALUE_TYPES.STRING,
    },
];

/** Extra fields for types with temporal data. */
const TEMPORAL_FIELDS = [
    {
        id: 'metadata.startDate',
        labelKey: 'discoverPage.layerWizard.filters.fields.startDate',
        labelDefault: 'Start date',
        valueType: VALUE_TYPES.DATE_TIME,
    },
    {
        id: 'metadata.endDate',
        labelKey: 'discoverPage.layerWizard.filters.fields.endDate',
        labelDefault: 'End date',
        valueType: VALUE_TYPES.DATE_TIME,
    },
];

/** Extra fields for venue-like types. */
const VENUE_EXTRA_FIELDS = [
    {
        id: 'metadata.address',
        labelKey: 'discoverPage.layerWizard.filters.fields.address',
        labelDefault: 'Address',
        valueType: VALUE_TYPES.STRING,
    },
    {
        id: 'metadata.phone',
        labelKey: 'discoverPage.layerWizard.filters.fields.phone',
        labelDefault: 'Phone',
        valueType: VALUE_TYPES.STRING,
    },
    {
        id: 'metadata.website',
        labelKey: 'discoverPage.layerWizard.filters.fields.website',
        labelDefault: 'Website',
        valueType: VALUE_TYPES.STRING,
    },
];

/** Extra fields for artist profiles. */
const ARTIST_EXTRA_FIELDS = [
    {
        id: 'metadata.stateOrRegion',
        labelKey: 'discoverPage.layerWizard.filters.fields.stateOrRegion',
        labelDefault: 'State/Region',
        valueType: VALUE_TYPES.STRING,
    },
    {
        id: 'metadata.country',
        labelKey: 'discoverPage.layerWizard.filters.fields.country',
        labelDefault: 'Country',
        valueType: VALUE_TYPES.STRING,
    },
    {
        id: 'metadata.location',
        labelKey: 'discoverPage.layerWizard.filters.fields.location',
        labelDefault: 'Location',
        valueType: VALUE_TYPES.STRING,
    },
    {
        id: 'metadata.preferredStyles',
        labelKey: 'discoverPage.layerWizard.filters.fields.preferredStyle',
        labelDefault: 'Preferred styles',
        valueType: VALUE_TYPES.STRING,
    },
    {
        id: 'metadata.instrumentExperience',
        labelKey: 'discoverPage.layerWizard.filters.fields.instrumentExperience',
        labelDefault: 'Instruments',
        valueType: VALUE_TYPES.STRING,
    },
    {
        id: 'metadata.influences',
        labelKey: 'discoverPage.layerWizard.filters.fields.influences',
        labelDefault: 'Influences',
        valueType: VALUE_TYPES.STRING,
    },
    {
        id: 'metadata.seeking',
        labelKey: 'discoverPage.layerWizard.filters.fields.seeking',
        labelDefault: 'Seeking',
        valueType: VALUE_TYPES.STRING,
    },
    {
        id: 'metadata.commitment',
        labelKey: 'discoverPage.layerWizard.filters.fields.commitment',
        labelDefault: 'Commitment',
        valueType: VALUE_TYPES.STRING,
    },
    {
        id: 'metadata.about',
        labelKey: 'discoverPage.layerWizard.filters.fields.about',
        labelDefault: 'About',
        valueType: VALUE_TYPES.STRING,
    },
    {
        id: 'metadata.equipment',
        labelKey: 'discoverPage.layerWizard.filters.fields.equipment',
        labelDefault: 'Equipment',
        valueType: VALUE_TYPES.STRING,
    },
    {
        id: 'metadata.timezone',
        labelKey: 'discoverPage.layerWizard.filters.fields.timezone',
        labelDefault: 'Timezone',
        valueType: VALUE_TYPES.STRING,
    },
];

/** Extra fields for band types. */
const BAND_EXTRA_FIELDS = [
    {
        id: 'metadata.stateOrRegion',
        labelKey: 'discoverPage.layerWizard.filters.fields.stateOrRegion',
        labelDefault: 'State/Region',
        valueType: VALUE_TYPES.STRING,
    },
    {
        id: 'metadata.country',
        labelKey: 'discoverPage.layerWizard.filters.fields.country',
        labelDefault: 'Country',
        valueType: VALUE_TYPES.STRING,
    },
    {
        id: 'metadata.website',
        labelKey: 'discoverPage.layerWizard.filters.fields.website',
        labelDefault: 'Website',
        valueType: VALUE_TYPES.STRING,
    },
];

/**
 * Allowlisted field catalog keyed by object type.
 * Only these fields may appear in filter rules.
 */
export const FIELD_CATALOG = Object.freeze({
    ARTIST: Object.freeze([...COMMON_FIELDS, ...ARTIST_EXTRA_FIELDS]),
    BAND: Object.freeze([...COMMON_FIELDS, ...BAND_EXTRA_FIELDS]),
    EVENT: Object.freeze([...COMMON_FIELDS, ...TEMPORAL_FIELDS]),
    SESSION: Object.freeze([...COMMON_FIELDS, ...TEMPORAL_FIELDS]),
    VENUE: Object.freeze([...COMMON_FIELDS, ...VENUE_EXTRA_FIELDS]),
});

// ---------------------------------------------------------------------------
// Backend field catalog fetch + cache
// ---------------------------------------------------------------------------

/** Module-level cache: objectType → { fields, popupFields } from backend. */
const _fieldCatalogCache = new Map();

/**
 * Fetch the field catalog from the backend for the given type.
 * Caches the result per type. Falls back to the hardcoded FIELD_CATALOG
 * if the fetch fails.
 *
 * @param {string} objectType - e.g. 'ARTIST'
 * @param {object} geoServiceRef - geoService instance with fetchFieldCatalog()
 * @returns {Promise<{ fields: Array, popupFields: Array }>}
 */
export async function fetchAndCacheFieldCatalog(objectType, geoServiceRef) {
    if (_fieldCatalogCache.has(objectType)) {
        return _fieldCatalogCache.get(objectType);
    }
    try {
        const response = await geoServiceRef.fetchFieldCatalog(objectType);
        const merged = {
            fields: (response.fields || []).map((f) => {
                // Merge with local label info if available
                const local = (FIELD_CATALOG[objectType] || []).find((lf) => lf.id === f.id);
                return {
                    id: f.id,
                    labelKey: local?.labelKey || `discoverPage.layerWizard.filters.fields.${f.id}`,
                    labelDefault: local?.labelDefault || f.id,
                    valueType: f.valueType,
                    operators: f.operators || OPERATORS_BY_TYPE[f.valueType] || [],
                };
            }),
            popupFields: (response.popupFields || []).map((pf) => pf.id),
        };
        _fieldCatalogCache.set(objectType, merged);
        return merged;
    } catch {
        // Fallback to hardcoded catalog
        const fields = (FIELD_CATALOG[objectType] || []).map((f) => ({
            ...f,
            operators: OPERATORS_BY_TYPE[f.valueType] || [],
        }));
        const stdIds = STANDARD_POPUP_FIELDS.map((f) => f.id);
        const metaIds = (METADATA_POPUP_FIELDS[objectType] || []).map((f) => f.id);
        const fallback = { fields, popupFields: [...stdIds, ...metaIds] };
        _fieldCatalogCache.set(objectType, fallback);
        return fallback;
    }
}

/**
 * Clear the field catalog cache (useful for testing).
 */
export function clearFieldCatalogCache() {
    _fieldCatalogCache.clear();
}

// ---------------------------------------------------------------------------
// Schema constants
// ---------------------------------------------------------------------------

export const FILTER_SCHEMA_VERSION = 1;
export const MAX_RULES = 20;
export const MAX_IN_VALUES = 20;

/** Match modes. */
export const MATCH_MODES = Object.freeze({ ALL: 'ALL', ANY: 'ANY' });

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Get the field catalog for a given object type.
 * Returns empty array for unknown types.
 */
export function getFieldsForType(objectType) {
    return FIELD_CATALOG[objectType] || [];
}

/**
 * Look up a field definition by id within a type's catalog.
 */
export function getFieldDef(objectType, fieldId) {
    const fields = getFieldsForType(objectType);
    return fields.find((f) => f.id === fieldId) || null;
}

/**
 * Get allowed operators for a given value type.
 */
export function getOperatorsForValueType(valueType) {
    return OPERATORS_BY_TYPE[valueType] || [];
}

/**
 * Whether the given operator requires a second value input (BETWEEN).
 */
export function operatorNeedsSecondValue(operator) {
    return operator === 'BETWEEN';
}

/**
 * Whether the given operator uses comma-separated list input (IN/NOT_IN).
 */
export function operatorUsesListInput(operator) {
    return operator === 'IN' || operator === 'NOT_IN';
}

/**
 * Whether the given operator takes no value (BOOLEAN operators).
 */
export function operatorTakesNoValue(operator) {
    return operator === 'IS_TRUE' || operator === 'IS_FALSE';
}

// ---------------------------------------------------------------------------
// Type-switch pruning
// ---------------------------------------------------------------------------

/**
 * Separate rules into those compatible with the given object type and those
 * that reference fields not in the type's catalog.
 *
 * Used when the user changes objectType mid-wizard — incompatible rules are
 * removed with explicit feedback rather than kept in a hidden invalid state.
 *
 * @param {Array} rules - Current rule drafts
 * @param {string} newObjectType - Target object type
 * @returns {{ kept: Array, pruned: Array }}
 */
export function pruneRulesForType(rules, newObjectType) {
    if (!Array.isArray(rules) || rules.length === 0) {
        return { kept: [], pruned: [] };
    }
    const fields = getFieldsForType(newObjectType);
    const fieldIds = new Set(fields.map((f) => f.id));

    const kept = [];
    const pruned = [];

    for (const rule of rules) {
        // Empty fieldId — rule hasn't been configured yet; keep it
        if (!rule.fieldId) {
            kept.push(rule);
            continue;
        }
        if (fieldIds.has(rule.fieldId)) {
            // Field exists in new type — check operator compatibility
            const fieldDef = fields.find((f) => f.id === rule.fieldId);
            const allowedOps = getOperatorsForValueType(fieldDef.valueType);
            if (rule.operator && !allowedOps.includes(rule.operator)) {
                // Operator incompatible — reset operator and values
                kept.push({ ...rule, operator: '', value: '', secondValue: '' });
            } else {
                kept.push(rule);
            }
        } else {
            pruned.push(rule);
        }
    }

    return { kept, pruned };
}

// ---------------------------------------------------------------------------
// Persisted-rule migration (deleted-field resilience)
// ---------------------------------------------------------------------------

/**
 * Migrate persisted/loaded rules against the current field catalog.
 *
 * Returns migrated rules with stale entries flagged, plus a list of warnings
 * for fields that no longer exist in the catalog. This supports the
 * "edited layer with stale fields shows deterministic remediation" requirement.
 *
 * @param {Array} rules - Stored rule objects
 * @param {string} objectType - The layer's object type
 * @returns {{ rules: Array, staleFieldIds: string[] }}
 */
export function migratePersistedRules(rules, objectType) {
    if (!Array.isArray(rules) || rules.length === 0) {
        return { rules: [], staleFieldIds: [] };
    }
    const fields = getFieldsForType(objectType);
    const fieldIds = new Set(fields.map((f) => f.id));
    const staleFieldIds = [];

    const migrated = rules.map((rule) => {
        if (!rule.fieldId) return rule;

        if (!fieldIds.has(rule.fieldId)) {
            if (!staleFieldIds.includes(rule.fieldId)) {
                staleFieldIds.push(rule.fieldId);
            }
            // Mark as stale so UI can flag it
            return { ...rule, _stale: true };
        }

        // Field exists — verify operator is still valid
        const fieldDef = fields.find((f) => f.id === rule.fieldId);
        const allowedOps = getOperatorsForValueType(fieldDef.valueType);
        if (rule.operator && !allowedOps.includes(rule.operator)) {
            return { ...rule, operator: '', value: '', secondValue: '', _stale: true };
        }

        return rule;
    });

    return { rules: migrated, staleFieldIds };
}

/**
 * Check whether any rules reference fields not in the current catalog.
 * Returns the count of stale rules (useful for blocking submit).
 *
 * @param {Array} rules
 * @param {string} objectType
 * @returns {number}
 */
export function countStaleRules(rules, objectType) {
    if (!Array.isArray(rules) || rules.length === 0) return 0;
    const fields = getFieldsForType(objectType);
    const fieldIds = new Set(fields.map((f) => f.id));
    return rules.filter((r) => r.fieldId && !fieldIds.has(r.fieldId)).length;
}

// ---------------------------------------------------------------------------
// Rule validation
// ---------------------------------------------------------------------------

/**
 * Validate a single filter rule.
 * @param {Object} rule - { fieldId, operator, value, secondValue }
 * @param {string} objectType - The layer's object type
 * @returns {string|null} Error key suffix or null if valid
 */
export function validateRule(rule, objectType) {
    if (!rule) return 'ruleFieldRequired';

    // Field must exist in catalog
    const fieldDef = getFieldDef(objectType, rule.fieldId);
    if (!fieldDef) return 'ruleFieldRequired';

    // Operator required and must be valid for field type
    if (!rule.operator) return 'ruleOperatorRequired';
    const allowedOps = getOperatorsForValueType(fieldDef.valueType);
    if (!allowedOps.includes(rule.operator)) return 'ruleInvalidOperator';

    // Boolean operators need no value
    if (operatorTakesNoValue(rule.operator)) return null;

    // IN/NOT_IN validation
    if (operatorUsesListInput(rule.operator)) {
        const raw = (rule.value ?? '').trim();
        if (!raw) return 'ruleValueRequired';
        const items = raw.split(',').map((s) => s.trim()).filter(Boolean);
        if (items.length === 0) return 'ruleValueRequired';
        if (items.length > MAX_IN_VALUES) return 'ruleInvalidValue';
        return null;
    }

    // BETWEEN validation
    if (operatorNeedsSecondValue(rule.operator)) {
        const v1 = (rule.value ?? '').trim();
        const v2 = (rule.secondValue ?? '').trim();
        if (!v1) return 'ruleValueRequired';
        if (!v2) return 'ruleSecondValueRequired';
        if (fieldDef.valueType === VALUE_TYPES.NUMBER) {
            const n1 = Number(v1);
            const n2 = Number(v2);
            if (Number.isNaN(n1) || Number.isNaN(n2)) return 'ruleInvalidValue';
            if (n1 > n2) return 'ruleBetweenOrder';
        } else if (fieldDef.valueType === VALUE_TYPES.DATE_TIME) {
            if (v1 > v2) return 'ruleBetweenOrder';
        }
        return null;
    }

    // Standard single-value validation
    const val = (rule.value ?? '').trim();
    if (!val) return 'ruleValueRequired';

    // Number type check
    if (fieldDef.valueType === VALUE_TYPES.NUMBER && Number.isNaN(Number(val))) {
        return 'ruleInvalidValue';
    }

    return null;
}

/**
 * Validate all rules and return per-rule error map.
 * @param {Array} rules
 * @param {string} objectType
 * @returns {{ errors: Object<number,string>, isValid: boolean }}
 */
export function validateAllRules(rules, objectType) {
    const errors = {};
    let isValid = true;
    if (!Array.isArray(rules)) return { errors, isValid: true };

    for (let i = 0; i < rules.length; i++) {
        const err = validateRule(rules[i], objectType);
        if (err) {
            errors[i] = err;
            isValid = false;
        }
    }
    return { errors, isValid };
}

// ---------------------------------------------------------------------------
// Popup field catalog
// ---------------------------------------------------------------------------

export const POPUP_SCHEMA_VERSION = 1;
export const MAX_POPUP_METADATA_FIELDS = 20;

/**
 * Standard popup fields (non-metadata). These are always available for any
 * object type and keyed by a stable id.
 */
export const STANDARD_POPUP_FIELDS = Object.freeze([
    { id: 'author', labelKey: 'discoverPage.layerWizard.popup.author', labelDefault: 'Author' },
    { id: 'visibility', labelKey: 'discoverPage.layerWizard.popup.visibility', labelDefault: 'Visibility' },
    { id: 'created', labelKey: 'discoverPage.layerWizard.popup.created', labelDefault: 'Created' },
]);

/**
 * Metadata popup fields keyed by object type.
 * Uses the same field ids as the filter catalog (metadata.x) to stay consistent.
 */
const METADATA_POPUP_FIELDS = Object.freeze({
    ARTIST: Object.freeze([
        { id: 'metadata.city', labelKey: 'discoverPage.layerWizard.filters.fields.city', labelDefault: 'City' },
        { id: 'metadata.stateOrRegion', labelKey: 'discoverPage.layerWizard.filters.fields.stateOrRegion', labelDefault: 'State/Region' },
        { id: 'metadata.country', labelKey: 'discoverPage.layerWizard.filters.fields.country', labelDefault: 'Country' },
        { id: 'metadata.location', labelKey: 'discoverPage.layerWizard.filters.fields.location', labelDefault: 'Location' },
        { id: 'metadata.preferredStyles', labelKey: 'discoverPage.layerWizard.filters.fields.preferredStyle', labelDefault: 'Preferred styles' },
        { id: 'metadata.instrumentExperience', labelKey: 'discoverPage.layerWizard.filters.fields.instrumentExperience', labelDefault: 'Instruments' },
        { id: 'metadata.influences', labelKey: 'discoverPage.layerWizard.filters.fields.influences', labelDefault: 'Influences' },
        { id: 'metadata.seeking', labelKey: 'discoverPage.layerWizard.filters.fields.seeking', labelDefault: 'Seeking' },
        { id: 'metadata.commitment', labelKey: 'discoverPage.layerWizard.filters.fields.commitment', labelDefault: 'Commitment' },
        { id: 'metadata.about', labelKey: 'discoverPage.layerWizard.filters.fields.about', labelDefault: 'About' },
        { id: 'metadata.equipment', labelKey: 'discoverPage.layerWizard.filters.fields.equipment', labelDefault: 'Equipment' },
        { id: 'metadata.timezone', labelKey: 'discoverPage.layerWizard.filters.fields.timezone', labelDefault: 'Timezone' },
        { id: 'metadata.description', labelKey: 'discoverPage.layerWizard.filters.fields.description', labelDefault: 'Description' },
    ]),
    BAND: Object.freeze([
        { id: 'metadata.city', labelKey: 'discoverPage.layerWizard.filters.fields.city', labelDefault: 'City' },
        { id: 'metadata.stateOrRegion', labelKey: 'discoverPage.layerWizard.filters.fields.stateOrRegion', labelDefault: 'State/Region' },
        { id: 'metadata.country', labelKey: 'discoverPage.layerWizard.filters.fields.country', labelDefault: 'Country' },
        { id: 'metadata.description', labelKey: 'discoverPage.layerWizard.filters.fields.description', labelDefault: 'Description' },
        { id: 'metadata.website', labelKey: 'discoverPage.layerWizard.filters.fields.website', labelDefault: 'Website' },
    ]),
    EVENT: Object.freeze([
        { id: 'metadata.startDate', labelKey: 'discoverPage.layerWizard.filters.fields.startDate', labelDefault: 'Start date' },
        { id: 'metadata.endDate', labelKey: 'discoverPage.layerWizard.filters.fields.endDate', labelDefault: 'End date' },
        { id: 'metadata.city', labelKey: 'discoverPage.layerWizard.filters.fields.city', labelDefault: 'City' },
        { id: 'metadata.description', labelKey: 'discoverPage.layerWizard.filters.fields.description', labelDefault: 'Description' },
    ]),
    SESSION: Object.freeze([
        { id: 'metadata.startDate', labelKey: 'discoverPage.layerWizard.filters.fields.startDate', labelDefault: 'Start date' },
        { id: 'metadata.endDate', labelKey: 'discoverPage.layerWizard.filters.fields.endDate', labelDefault: 'End date' },
        { id: 'metadata.city', labelKey: 'discoverPage.layerWizard.filters.fields.city', labelDefault: 'City' },
        { id: 'metadata.description', labelKey: 'discoverPage.layerWizard.filters.fields.description', labelDefault: 'Description' },
    ]),
    VENUE: Object.freeze([
        { id: 'metadata.address', labelKey: 'discoverPage.layerWizard.filters.fields.address', labelDefault: 'Address' },
        { id: 'metadata.city', labelKey: 'discoverPage.layerWizard.filters.fields.city', labelDefault: 'City' },
        { id: 'metadata.phone', labelKey: 'discoverPage.layerWizard.filters.fields.phone', labelDefault: 'Phone' },
        { id: 'metadata.website', labelKey: 'discoverPage.layerWizard.filters.fields.website', labelDefault: 'Website' },
        { id: 'metadata.description', labelKey: 'discoverPage.layerWizard.filters.fields.description', labelDefault: 'Description' },
    ]),
});

/**
 * Get metadata popup fields for a given object type.
 */
export function getPopupMetadataFields(objectType) {
    return METADATA_POPUP_FIELDS[objectType] || [];
}

/**
 * Get all popup field ids that are valid for a given type.
 */
export function getValidPopupFieldIds(objectType) {
    const standardIds = STANDARD_POPUP_FIELDS.map((f) => f.id);
    const metadataIds = getPopupMetadataFields(objectType).map((f) => f.id);
    return new Set([...standardIds, ...metadataIds]);
}

/**
 * Build default popup config for a given type.
 * By default, all standard fields are enabled in catalog order.
 */
export function buildDefaultPopupFields(objectType) {
    const standardIds = STANDARD_POPUP_FIELDS.map((f) => f.id);
    const metadataIds = getPopupMetadataFields(objectType).map((f) => f.id);
    return [...standardIds, ...metadataIds];
}

/**
 * Prune popup field selection to only fields valid for the given type.
 * Returns { kept, pruned } arrays of field ids.
 */
export function prunePopupFieldsForType(selectedFieldIds, objectType) {
    if (!Array.isArray(selectedFieldIds) || selectedFieldIds.length === 0) {
        return { kept: [], pruned: [] };
    }
    const validIds = getValidPopupFieldIds(objectType);
    const kept = [];
    const pruned = [];
    for (const id of selectedFieldIds) {
        if (validIds.has(id)) {
            kept.push(id);
        } else {
            pruned.push(id);
        }
    }
    return { kept, pruned };
}

/**
 * Validate popup field selection.
 * @returns {string|null} Error key or null if valid.
 */
export function validatePopupFields(selectedFieldIds, objectType) {
    if (!Array.isArray(selectedFieldIds) || selectedFieldIds.length === 0) {
        return 'requireOneField';
    }
    const metadataIds = getPopupMetadataFields(objectType).map((f) => f.id);
    const metadataSet = new Set(metadataIds);
    const selectedMetadata = selectedFieldIds.filter((id) => metadataSet.has(id));
    if (selectedMetadata.length > MAX_POPUP_METADATA_FIELDS) {
        return 'metadataLimit';
    }
    return null;
}

/**
 * Serialize popup config to the canonical schema for persistence.
 */
export function serializePopupSchema(selectedFieldIds, hideEmpty) {
    return {
        schemaVersion: POPUP_SCHEMA_VERSION,
        fields: Array.isArray(selectedFieldIds) ? [...selectedFieldIds] : [],
        hideEmpty: Boolean(hideEmpty),
    };
}

// ---------------------------------------------------------------------------
// Filter serialization
// ---------------------------------------------------------------------------

/**
 * Serialize filter rules to the canonical schema for persistence.
 * @param {string} matchMode - 'ALL' or 'ANY'
 * @param {Array} rules
 * @returns {Object} Canonical filters_json payload
 */
export function serializeFilterSchema(matchMode, rules) {
    return {
        schemaVersion: FILTER_SCHEMA_VERSION,
        match: matchMode === MATCH_MODES.ANY ? MATCH_MODES.ANY : MATCH_MODES.ALL,
        rules: (rules || []).map((r) => {
            const serialized = {
                fieldId: r.fieldId,
                operator: r.operator,
            };

            if (operatorTakesNoValue(r.operator)) {
                return serialized;
            }

            if (operatorUsesListInput(r.operator)) {
                const items = (r.value ?? '').split(',')
                    .map((s) => s.trim())
                    .filter(Boolean);
                // Deduplicate
                serialized.value = [...new Set(items)];
                return serialized;
            }

            serialized.value = (r.value ?? '').trim();

            if (operatorNeedsSecondValue(r.operator)) {
                serialized.secondValue = (r.secondValue ?? '').trim();
            }

            return serialized;
        }),
    };
}
